import { EquipmentSurveyInput as EquipmentSurveyAPI } from "gql/graphql";
import { useSubmitEquipmentData } from "queries/Equipment/useSubmitEquipmentSurvey";
import React, { useCallback, useState } from "react";
import { useParams } from "react-router-dom";
import useBuildingLoadingState from "recoilStore/useBuildingLoadingState";
import {
    ActiveView,
    DEFAULT_AIR_HANDLING_EQUIPMENT,
    DEFAULT_CENTRAL_PLANT_COOLER,
    DEFAULT_CENTRAL_PLANT_GENERIC_EQUIPMENT,
    DEFAULT_CENTRAL_PLANT_HEAT_PUMP,
    DEFAULT_CENTRAL_PLANT_HEATER,
    DEFAULT_DOMESTIC_HOT_WATER,
    DEFAULT_HEAT_PUMP,
    DEFAULT_OTHER_EQUIPMENT,
    DEFAULT_ROOFTOP_UNIT,
    DEFAULT_TERMINAL_COOLER,
    DEFAULT_TERMINAL_HEATER,
    FURNACES,
    NONE,
    PTAC_HEATERS,
} from "./Survey.constants";
import { surveyValueExists, syncEquipmentExistsFlagsFromSelections } from "./Survey.helpers";
import { useEquipmentSurveyOpenHydration } from "./Survey.hydration";
import { useDefaultEquipmentSurveyTypes, useQueryOnStatusUpdate } from "./Survey.hooks";

import AirHandlingEquipmentSurveyView from "./Steps/AirHandlingEquipmentSurveyView";
import CentralPlantSurveyView from "./Steps/CentralPlantSurveyView";
import DomesticHotWaterHeaterSurvey from "./Steps/DomesticHotWaterHeaterSurvey";
import EquipmentSizeAndInstallationYearSurvey from "./Steps/EquipmentSizeAndInstallationYearSurvey";
import OtherEquipmentSurvey from "./Steps/OtherEquipmentSurvey";
import RoofTopUnitsSurveyView from "./Steps/RoofTopUnitsSurveyView";
import TerminalUnitsSurveyView from "./Steps/TerminalUnitsSurveyView";
import SingleFormEquipmentSurveyModal from "./SurveyElements/Equipment/SingleFormEquipmentSurveyModal";

interface SurveyProps {
    setShowSurveyView: React.Dispatch<React.SetStateAction<boolean>>;
    singleFormModalOpen?: boolean;
    setSingleFormModalOpen?: React.Dispatch<React.SetStateAction<boolean>>;
    showSurveyView?: boolean;
    persistedEquipmentSurvey?: EquipmentSurvey | null;
    agentPrefillEquipmentSurvey?: Partial<EquipmentSurvey> | null;
}

export function Survey({
    setShowSurveyView,
    singleFormModalOpen = false,
    setSingleFormModalOpen,
    showSurveyView = false,
    persistedEquipmentSurvey = null,
    agentPrefillEquipmentSurvey = null,
}: SurveyProps) {
    const [currentActiveView, setCurrentActiveView] = useState<ActiveView>(ActiveView.rooftopUnit);
    const [pageStack, setPageStack] = useState<ActiveView[]>([]);

    const [booleanQuestionResultSizeAndYearSurvey, setBooleanQuestionResultSizeAndYearSurvey] = useState<
        boolean | null
    >(null);

    const {
        rooftopUnit,
        setRooftopUnit,
        airHandlingEquipment,
        setAirHandlingEquipment,
        centralPlantCooler,
        setCentralPlantCooler,
        centralPlantHeater,
        setCentralPlantHeater,
        centralPlantHeatPump,
        setCentralPlantHeatPump,
        centralPlantGenericEquipment,
        setCentralPlantGenericEquipment,
        terminalCooler,
        setTerminalCooler,
        terminalHeater,
        setTerminalHeater,
        terminalHeatPump,
        setTerminalHeatPump,
        domesticHotWaterHeater,
        setDomesticHotWaterHeater,
        otherEquipment,
        setOtherEquipment,
    } = useDefaultEquipmentSurveyTypes();

    const pushEquipmentSurveyToFormState = useCallback(
        (data: EquipmentSurvey) => {
            setRooftopUnit(data.rooftopUnit);
            setAirHandlingEquipment(data.airHandlingEquipment);
            setCentralPlantCooler(data.centralPlantCooler);
            setCentralPlantHeater(data.centralPlantHeater);
            setCentralPlantHeatPump(data.centralPlantHeatPump);
            setCentralPlantGenericEquipment(data.centralPlantGenericEquipment);
            setTerminalCooler(data.terminalCooler);
            setTerminalHeater(data.terminalHeater);
            setTerminalHeatPump(data.heatPump);
            setDomesticHotWaterHeater(data.domesticHotWaterHeater);
            setOtherEquipment(data.otherEquipment);
        },
        [
            setRooftopUnit,
            setAirHandlingEquipment,
            setCentralPlantCooler,
            setCentralPlantHeater,
            setCentralPlantHeatPump,
            setCentralPlantGenericEquipment,
            setTerminalCooler,
            setTerminalHeater,
            setTerminalHeatPump,
            setDomesticHotWaterHeater,
            setOtherEquipment,
        ]
    );

    const resetWizardNavigation = useCallback(() => {
        setCurrentActiveView(ActiveView.rooftopUnit);
        setPageStack([]);
        setBooleanQuestionResultSizeAndYearSurvey(null);
    }, []);

    const resetFormToDefaults = useCallback(() => {
        resetWizardNavigation();
        setRooftopUnit({ ...DEFAULT_ROOFTOP_UNIT });
        setAirHandlingEquipment({ ...DEFAULT_AIR_HANDLING_EQUIPMENT });
        setCentralPlantCooler({ ...DEFAULT_CENTRAL_PLANT_COOLER });
        setCentralPlantHeater({ ...DEFAULT_CENTRAL_PLANT_HEATER });
        setCentralPlantHeatPump({ ...DEFAULT_CENTRAL_PLANT_HEAT_PUMP });
        setCentralPlantGenericEquipment({ ...DEFAULT_CENTRAL_PLANT_GENERIC_EQUIPMENT });
        setTerminalCooler({ ...DEFAULT_TERMINAL_COOLER });
        setTerminalHeater({ ...DEFAULT_TERMINAL_HEATER });
        setTerminalHeatPump({ ...DEFAULT_HEAT_PUMP });
        setDomesticHotWaterHeater({ ...DEFAULT_DOMESTIC_HOT_WATER });
        setOtherEquipment({ ...DEFAULT_OTHER_EQUIPMENT });
    }, [
        setRooftopUnit,
        setAirHandlingEquipment,
        setCentralPlantCooler,
        setCentralPlantHeater,
        setCentralPlantHeatPump,
        setCentralPlantGenericEquipment,
        setTerminalCooler,
        setTerminalHeater,
        setTerminalHeatPump,
        setDomesticHotWaterHeater,
        setOtherEquipment,
        resetWizardNavigation,
    ]);

    useEquipmentSurveyOpenHydration({
        showSurveyView,
        singleFormModalOpen,
        persistedEquipmentSurvey,
        agentPrefillEquipmentSurvey,
        pushEquipmentSurveyToFormState,
        resetFormToDefaults,
        resetWizardNavigation,
    });

    const { buildingModelUid } = useParams();
    if (!buildingModelUid) throw new Error("buildingModelUid should be defined");

    const { setLoadingState } = useBuildingLoadingState(buildingModelUid);
    const { onSuccess, onError } = useQueryOnStatusUpdate(buildingModelUid);
    const { mutate: submitEquipmentSurvey } = useSubmitEquipmentData(buildingModelUid, onSuccess, onError);

    const handleSaveSurvey = () => {
        let equipmentData: EquipmentSurvey = {
            rooftopUnit,
            airHandlingEquipment,
            centralPlantCooler,
            centralPlantHeater,
            centralPlantHeatPump,
            centralPlantGenericEquipment,
            terminalCooler,
            terminalHeater,
            genericHVACEquipment: [],
            heatPump: terminalHeatPump,
            domesticHotWaterHeater,
            otherEquipment,
        };

        const heaterTerminalUnits = equipmentData.centralPlantHeater.centralPlantHeaterTerminalUnits;
        if (
            (heaterTerminalUnits === "water_loop_heat_pump" || heaterTerminalUnits === "split_air_source_heat_pump") &&
            !surveyValueExists(equipmentData.heatPump.heatPumpType)
        ) {
            equipmentData = {
                ...equipmentData,
                heatPump: { ...equipmentData.heatPump, heatPumpType: heaterTerminalUnits },
            };
        }

        equipmentData = syncEquipmentExistsFlagsFromSelections(equipmentData);

        Object.keys(equipmentData).forEach((key) => {
            const obj = equipmentData[key as keyof EquipmentSurvey] as any;
            Object.keys(obj).forEach((sectionKey) => {
                if (
                    (obj[sectionKey] === "water_loop_heat_pump" || obj[sectionKey] === "split_air_source_heat_pump") &&
                    sectionKey === "centralPlantHeaterTerminalUnits"
                ) {
                    obj[sectionKey] = null;
                }

                if (obj[sectionKey] === NONE) obj[sectionKey] = null;
                if (sectionKey.toLowerCase().includes("exists") && obj[sectionKey] === null) obj[sectionKey] = false;
            });
        });

        if (
            PTAC_HEATERS.includes(equipmentData.terminalHeater.terminalHeaterUnits) &&
            equipmentData.terminalCooler.terminalCoolerExists &&
            equipmentData.terminalHeater.terminalHeaterAverageInstallationYear != null
        ) {
            equipmentData.terminalCooler = {
                ...equipmentData.terminalCooler,
                terminalCoolerAverageInstallationYear:
                    equipmentData.terminalHeater.terminalHeaterAverageInstallationYear,
            };
        }

        if (FURNACES.includes(equipmentData.terminalHeater.terminalHeaterUnits)) {
            equipmentData.centralPlantHeater = {
                ...DEFAULT_CENTRAL_PLANT_HEATER,
                centralPlantHeaterExists: true,
                centralPlantHeaterType: equipmentData.terminalHeater.terminalHeaterUnits,
                centralPlantHeaterSize: equipmentData.terminalHeater.terminalHeaterSize,
                centralPlantHeaterAverageInstallationYear:
                    equipmentData.terminalHeater.terminalHeaterAverageInstallationYear,
            };
            equipmentData.terminalHeater = {
                ...DEFAULT_TERMINAL_HEATER,
                terminalHeaterExists: false,
            };
        }

        if (equipmentData.centralPlantGenericEquipment.centralPlantGenericEquipmentExists) {
            equipmentData.genericHVACEquipment.push({
                name: equipmentData.centralPlantGenericEquipment.centralPlantGenericEquipmentName!,
                size: equipmentData.centralPlantGenericEquipment.centralPlantGenericEquipmentSize!,
                sizeUnits: equipmentData.centralPlantGenericEquipment.centralPlantGenericEquipmentSizeUnits!,
                coefficientOfPerformance:
                    equipmentData.centralPlantGenericEquipment.centralPlantGenericEquipmentCoefficientOfPerformance!,
                fuel: equipmentData.centralPlantGenericEquipment.centralPlantGenericEquipmentFuel!,
                endUse: equipmentData.centralPlantGenericEquipment.centralPlantGenericEquipmentEndUse!,
                loadRatio: equipmentData.centralPlantGenericEquipment.centralPlantGenericEquipmentLoadRatio!,
                installationYear:
                    equipmentData.centralPlantGenericEquipment.centralPlantGenericEquipmentInstallationYear!,
                lifeSpan: 15,
            });
        }

        pushEquipmentSurveyToFormState(equipmentData);

        setLoadingState((prev) => ({
            ...prev,
            equipmentSidebarLoading: true,
            buildingReportsLoading: true,
        }));

        const payload = { ...equipmentData } as Partial<EquipmentSurvey>;
        delete payload.centralPlantGenericEquipment;
        submitEquipmentSurvey(payload as EquipmentSurveyAPI);
        setShowSurveyView(false);
        setSingleFormModalOpen?.(false);
    };

    return (
        <>
            <SingleFormEquipmentSurveyModal
                open={singleFormModalOpen}
                onClose={() => setSingleFormModalOpen?.(false)}
                onSave={handleSaveSurvey}
                rooftopUnit={rooftopUnit}
                setRooftopUnit={setRooftopUnit}
                airHandlingEquipment={airHandlingEquipment}
                setAirHandlingEquipment={setAirHandlingEquipment}
                centralPlantCooler={centralPlantCooler}
                setCentralPlantCooler={setCentralPlantCooler}
                centralPlantHeater={centralPlantHeater}
                setCentralPlantHeater={setCentralPlantHeater}
                centralPlantHeatPump={centralPlantHeatPump}
                setCentralPlantHeatPump={setCentralPlantHeatPump}
                terminalCooler={terminalCooler}
                setTerminalCooler={setTerminalCooler}
                terminalHeater={terminalHeater}
                setTerminalHeater={setTerminalHeater}
                terminalHeatPump={terminalHeatPump}
                setTerminalHeatPump={setTerminalHeatPump}
                domesticHotWaterHeater={domesticHotWaterHeater}
                setDomesticHotWaterHeater={setDomesticHotWaterHeater}
                otherEquipment={otherEquipment}
                setOtherEquipment={setOtherEquipment}
                centralPlantGenericEquipment={centralPlantGenericEquipment}
                setCentralPlantGenericEquipment={setCentralPlantGenericEquipment}
            />
            {currentActiveView === ActiveView.rooftopUnit && (
                <RoofTopUnitsSurveyView
                    roofTopUnit={rooftopUnit}
                    setRooftopUnit={setRooftopUnit}
                    setActiveView={setCurrentActiveView}
                    activeView={currentActiveView}
                    airHandlingEquipment={airHandlingEquipment}
                    setPageStack={setPageStack}
                />
            )}
            {currentActiveView === ActiveView.airHandlingEquipment && (
                <AirHandlingEquipmentSurveyView
                    airHandlingEquipment={airHandlingEquipment}
                    setAirHandlingEquipment={setAirHandlingEquipment}
                    rooftopUnit={rooftopUnit}
                    setActiveView={setCurrentActiveView}
                    activeView={currentActiveView}
                    pageStack={pageStack}
                    setPageStack={setPageStack}
                />
            )}
            {currentActiveView === ActiveView.centralPlantHeaterCooler && (
                <CentralPlantSurveyView
                    setActiveView={setCurrentActiveView}
                    activeView={currentActiveView}
                    centralPlantCooler={centralPlantCooler}
                    setCentralPlantCooler={setCentralPlantCooler}
                    centralPlantHeater={centralPlantHeater}
                    setCentralPlantHeater={setCentralPlantHeater}
                    centralPlantHeatPump={centralPlantHeatPump}
                    setCentralPlantHeatPump={setCentralPlantHeatPump}
                    setHeatPump={setTerminalHeatPump}
                    airHandlingEquipment={airHandlingEquipment}
                    centralPlantGenericEquipment={centralPlantGenericEquipment}
                    setCentralPlantGenericEquipment={setCentralPlantGenericEquipment}
                    pageStack={pageStack}
                    setPageStack={setPageStack}
                />
            )}
            {currentActiveView === ActiveView.terminalUnits && (
                <TerminalUnitsSurveyView
                    terminalCooler={terminalCooler}
                    setTerminalCooler={setTerminalCooler}
                    terminalHeater={terminalHeater}
                    setTerminalHeater={setTerminalHeater}
                    airHandlingEquipment={airHandlingEquipment}
                    roofTopUnit={rooftopUnit}
                    centralPlantCooler={centralPlantCooler}
                    centralPlantHeater={centralPlantHeater}
                    setActiveView={setCurrentActiveView}
                    activeView={currentActiveView}
                    pageStack={pageStack}
                    setPageStack={setPageStack}
                />
            )}
            {currentActiveView === ActiveView.domesticHotWaterHeater && (
                <DomesticHotWaterHeaterSurvey
                    domesticHotWaterHeater={domesticHotWaterHeater}
                    centralPlantHeater={centralPlantHeater}
                    setDomesticHotWaterHeater={setDomesticHotWaterHeater}
                    setActiveView={setCurrentActiveView}
                    activeView={currentActiveView}
                    pageStack={pageStack}
                    setPageStack={setPageStack}
                />
            )}
            {currentActiveView === ActiveView.otherEquipment && (
                <OtherEquipmentSurvey
                    otherEquipment={otherEquipment}
                    setOtherEquipment={setOtherEquipment}
                    setActiveView={setCurrentActiveView}
                    activeView={currentActiveView}
                    pageStack={pageStack}
                    setPageStack={setPageStack}
                />
            )}
            {currentActiveView === ActiveView.sizeAndInstallationYear && (
                <EquipmentSizeAndInstallationYearSurvey
                    rooftopUnit={rooftopUnit}
                    setRooftopUnit={setRooftopUnit}
                    airHandlingEquipment={airHandlingEquipment}
                    setAirHandlingEqipment={setAirHandlingEquipment}
                    centralPlantCooler={centralPlantCooler}
                    setCentralPlantCooler={setCentralPlantCooler}
                    centralPlantHeater={centralPlantHeater}
                    setCentralPlantHeater={setCentralPlantHeater}
                    centralPlantHeatPump={centralPlantHeatPump}
                    setCentralPlantHeatPump={setCentralPlantHeatPump}
                    centralPlantGenericEquipment={centralPlantGenericEquipment}
                    setCentralPlantGenericEquipment={setCentralPlantGenericEquipment}
                    terminalCooler={terminalCooler}
                    setTerminalCooler={setTerminalCooler}
                    terminalHeater={terminalHeater}
                    setTerminalHeater={setTerminalHeater}
                    terminalHeatPump={terminalHeatPump}
                    setTerminalHeatPump={setTerminalHeatPump}
                    domesticHotWaterHeater={domesticHotWaterHeater}
                    setDomesticHotWaterHeater={setDomesticHotWaterHeater}
                    otherEquipment={otherEquipment}
                    setOtherEquipment={setOtherEquipment}
                    setActiveView={setCurrentActiveView}
                    setShowSurveyView={setShowSurveyView}
                    booleanQuestionResult={booleanQuestionResultSizeAndYearSurvey}
                    setBooleanQuestionResult={setBooleanQuestionResultSizeAndYearSurvey}
                    pageStack={pageStack}
                    setPageStack={setPageStack}
                    onSaveSurvey={handleSaveSurvey}
                />
            )}
        </>
    );
}
